import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Download, Calendar, Search, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { printConsignmentRegister } from "@/lib/print";
import { parseTermDate, parseTermMonth } from "@/lib/format";
import { HEAD_OFFICE } from "@/lib/company";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const bookingDate = (r) => r.lr_date || r.dispatch_date || (r.created_at ? String(r.created_at).slice(0, 10) : "");
const isDelivered = (r) => /deliver|complete|paid/i.test(r.status || "");
import { BRANCHES as FIXED_BRANCHES, branchPillClass } from "@/lib/branches";

const chequeDetails = (r) => {
  if (!r.payment_mode || !["DD", "Cheque"].includes(r.payment_mode)) return "";
  const parts = [r.cheque_no ? `No. ${r.cheque_no}` : "", r.cheque_date ? fmt(r.cheque_date) : "", Number(r.cheque_amount) ? inr(r.cheque_amount) : "", r.cheque_bank || ""].filter(Boolean);
  return parts.join(" · ");
};

export default function BookingReport() {
  const [rows, setRows] = useState(null);
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [branch, setBranch] = useState("All");
  const [customer, setCustomer] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [q, setQ] = useState("");
  const [customersList, setCustomersList] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    api
      .get("/shipments")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/customers").then((r) => setCustomersList(r.data || [])).catch(() => {});
    api.get("/invoices").then((r) => setInvoices(r.data || [])).catch(() => {});
  }, []);

  const invByShipment = useMemo(() => {
    const norm = (v) => String(v || "").replace(/^(CN|INV)[-\s]?/i, "").replace(/^0+/, "").trim().toLowerCase();
    const byLr = {};
    const byId = {};
    invoices.forEach((inv) => {
      const ids = [...(inv.shipment_id ? [inv.shipment_id] : []), ...(Array.isArray(inv.shipment_ids) ? inv.shipment_ids : [])];
      ids.forEach((sid) => { if (sid && !byId[sid]) byId[sid] = inv; });
      const lrs = [inv.lr_no, ...(Array.isArray(inv.items) ? inv.items.map((it) => it.lr_no) : [])];
      lrs.forEach((lr) => { const k = norm(lr); if (k && !byLr[k]) byLr[k] = inv; });
    });
    return { byLr, byId, norm };
  }, [invoices]);
  const billFor = (r) => invByShipment.byLr[invByShipment.norm(r.lr_no || r.consignment_no)] || invByShipment.byId[r.id] || null;
  const withBill = (r) => {
    const b = billFor(r);
    return { ...r, _bill_no: b ? String(b.invoice_no || "").replace(/^INV[-\s]?/i, "") : "", _bill_date: b?.issue_date || "", _bill_amount: b ? b.total : null, _bill_key: b ? b.id || b.invoice_no : "" };
  };

  const codeByName = useMemo(() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  }, [customersList]);
  const custCode = (r) => codeByName[String(r.consignor || r.customer_name || "").trim().toLowerCase()] || "";

  const branches = useMemo(() => {
    const fromData = (rows || []).map((r) => r.office).filter(Boolean);
    return Array.from(new Set([...FIXED_BRANCHES, ...fromData]));
  }, [rows]);
  const customers = useMemo(() => Array.from(new Set((rows || []).map((r) => r.consignor || r.customer_name).filter(Boolean))).sort(), [rows]);
  const useRange = !!(fromDate && toDate);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows
      .filter((r) => {
        const d = bookingDate(r);
        if (!d) return false;
        const needle = q.trim().toLowerCase();
        const tDate = needle ? parseTermDate(needle) : null;
        const tMonth = needle && !tDate ? parseTermMonth(needle) : null;
        if (tDate) {
          if (d.slice(0, 10) !== tDate) return false;
        } else if (tMonth) {
          if (d.slice(0, 7) !== tMonth) return false;
        } else if (useRange) {
          if (d < fromDate || d > toDate) return false;
        } else if (d.slice(0, 7) !== month) return false;
        if (branch !== "All" && r.office !== branch) return false;
        if (customer !== "All" && (r.consignor || r.customer_name) !== customer) return false;
        if (statusFilter === "Delivered" && !isDelivered(r)) return false;
        if (statusFilter === "Pending" && isDelivered(r)) return false;
        if (unbilledOnly && billFor(r)) return false;
        if (needle && !tDate && !tMonth) {
          const hay = [r.consignor, r.customer_name, custCode(r), String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""), r.origin, r.destination, r.consignee, r.vehicle_no];
          if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
        }
        return true;
      })
      .sort((a, b) => (bookingDate(a) < bookingDate(b) ? -1 : 1));
  }, [rows, month, fromDate, toDate, useRange, branch, customer, statusFilter, q, codeByName, unbilledOnly, invByShipment]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalFreight = filtered.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
  const PER_PAGE = 20;
  const [pageNo, setPageNo] = useState(1);
  const [jumpTo, setJumpTo] = useState("");
  useEffect(() => { setPageNo(1); }, [filtered]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const page = Math.min(pageNo, totalPages);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const allSel = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((p) => { const n = new Set(p); if (allSel) filtered.forEach((r) => n.delete(r.id)); else filtered.forEach((r) => n.add(r.id)); return n; });
  const selectedRows = filtered.filter((r) => selected.has(r.id));
  const deliveredCount = filtered.filter(isDelivered).length;
  const pendingCount = filtered.length - deliveredCount;
  const topRoutes = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      const key = `${r.origin || "?"} → ${r.destination || "?"}`;
      m[key] = (m[key] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [filtered]);
  const [y, m] = month.split("-");
  const rFrom = useRange ? fromDate : `${month}-01`;
  const rTo = useRange ? toDate : `${month}-${new Date(Number(y), Number(m), 0).getDate()}`;
  const periodLabel = useRange
    ? `${fmt(fromDate)} — ${fmt(toDate)}`
    : new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const waBookingSummary = () => {
    if (!filtered.length) { toast.info("No bookings in this period"); return; }
    const byBranch = {};
    filtered.forEach((r) => { const b = r.office || "Chennai"; byBranch[b] = (byBranch[b] || 0) + 1; });
    const branchLine = Object.entries(byBranch).sort((a, b) => b[1] - a[1]).map(([b, n]) => `${b}: ${n}`).join(" · ");
    const routes = topRoutes.map(([k, n], i) => `${i + 1}. ${k} — ${n} booking${n === 1 ? "" : "s"}`).join("\n");
    const msg = `*NISHA TRANSPORT CORPORATION*\n*BOOKING SUMMARY — ${periodLabel.toUpperCase()}*\n\n*Total Bookings:* ${filtered.length}\n*Total Freight:* ₹${totalFreight.toLocaleString("en-IN")}\n*Delivered:* ${deliveredCount} · *Pending:* ${pendingCount}\n*By Branch:* ${branchLine}\n\n*Top Routes:*\n${routes || "—"}`;
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const waUnbilled = () => {
    const gaps = filtered.filter((r) => !billFor(r));
    if (!gaps.length) { toast.success("No billing gaps — every booking in this period has a bill"); return; }
    const lines = gaps.map((r, i) => `${i + 1}. LR ${String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "")} | ${bookingDate(r) ? fmt(bookingDate(r)) : "—"} | ${r.consignor || r.customer_name || "—"} | ${r.origin || "—"} → ${r.destination || "—"} | ₹${Number(r.freight_amount || 0).toLocaleString("en-IN")}`);
    const totalF = gaps.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
    const msg = `*NISHA TRANSPORT CORPORATION*\n*UNBILLED LRs — ${periodLabel.toUpperCase()}*\n\n*Pending billing:* ${gaps.length} LR(s) · Freight ₹${totalF.toLocaleString("en-IN")}\n\n${lines.join("\n")}\n\nPlease raise bills for the above at the earliest.`;
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const exportCsv = () => {
    const header = "Sl,Branch,LR No.,LR Date,From,Destination,Consignor Name,Customer Code,Consignee Name,Basis,Payment Mode,Cheque/DD Details,Vehicle No.,Vehicle Type,PKG,Invoice No.,Invoice Date,Invoice Value,Freight Amount,Bill No.,Bill Date,Bill Amount,Delivery Date,Delivery Status";
    const csv = [
      header,
      ...filtered.map((r, i) =>
        [i + 1, r.office || "Chennai", r.lr_no || r.consignment_no, bookingDate(r), r.origin, r.destination, r.consignor, custCode(r), r.consignee, r.basis || "", r.basis === "PAID" ? r.payment_mode || "" : "", chequeDetails(r), r.vehicle_no, r.vehicle_type || "", r.packages || "", r.invoice_no || "", r.invoice_date || "", r.invoice_value || "", r.freight_amount, String(billFor(r)?.invoice_no || "").replace(/^INV[-\s]?/i, ""), billFor(r)?.issue_date || "", billFor(r)?.total ?? "", r.delivery_date || "", r.status]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
      `"","","","","","","","","","","TOTAL","${totalFreight}",""`,
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `booking-report-${useRange ? `${fromDate}_to_${toDate}` : month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Booking Report"
        subtitle="Monthly / date-range consignment booking details."
        action={
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  className="w-[190px] pl-8 pr-7"
                  placeholder="Customer / code / LR"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="booking-search"
                />
                {q && (
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="booking-search-clear">
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input type="month" className="w-[150px] pl-8" value={month} onChange={(e) => setMonth(e.target.value)} disabled={useRange} data-testid="booking-month" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">From</Label>
              <Input type="date" className="w-[140px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="booking-from" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">To</Label>
              <Input type="date" className="w-[140px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="booking-to" />
            </div>
            {useRange && (
              <Button variant="ghost" size="sm" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="booking-clear-range">Clear range</Button>
            )}
            <div className="grid gap-1.5">
              <Label className="text-xs">Branch</Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger className="w-[140px]" data-testid="booking-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Customer</Label>
              <Select value={customer} onValueChange={setCustomer}>
                <SelectTrigger className="w-[170px]" data-testid="booking-customer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Customers</SelectItem>
                  {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="booking-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="ghost"
              className={`h-10 text-xs font-bold ${unbilledOnly ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-amber-300 bg-white text-amber-700 hover:bg-amber-50"}`}
              onClick={() => setUnbilledOnly((v) => !v)}
              data-testid="booking-unbilled-toggle"
            >
              {unbilledOnly ? "✓ " : ""}Unbilled only ({filtered.filter((r) => !billFor(r)).length})
            </Button>
            <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={waUnbilled} data-testid="booking-wa-unbilled-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Unbilled
            </Button>
            <Button variant="outline" onClick={exportCsv} data-testid="booking-export-btn">
              <Download className="mr-2 h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={waBookingSummary} data-testid="booking-wa-summary-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
            </Button>
            <Button variant="secondary" onClick={() => selectedRows.length ? printConsignmentRegister(selectedRows.map(withBill), rFrom, rTo, "MONTHLY BOOKING REPORT", { bookingCols: true, branch: branch === "All" ? "All Branches" : branch, customer: customer === "All" ? "All Customers" : customer, total: selectedRows.length, delivered: selectedRows.filter(isDelivered).length, pending: selectedRows.length - selectedRows.filter(isDelivered).length, topRoutes }) : toast.info("Select at least one booking")} disabled={!selectedRows.length} data-testid="booking-print-selected-btn">
              <Printer className="mr-2 h-4 w-4" /> Print Selected{selectedRows.length ? ` (${selectedRows.length})` : ""}
            </Button>
            <Button onClick={() => printConsignmentRegister(filtered.map((r) => withBill({ ...r, customer_code: r.customer_code || custCode(r), customer_name: r.customer_name || r.consignor || "" })), rFrom, rTo, "MONTHLY BOOKING REPORT", { bookingCols: true, branch: branch === "All" ? "All Branches" : branch, customer: customer === "All" ? "All Customers" : customer, total: filtered.length, delivered: deliveredCount, pending: pendingCount, topRoutes })} data-testid="booking-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </div>
        }
      />

      {rows && (() => {
        const byMonth = {};
        rows.forEach((r) => {
          const d = bookingDate(r);
          if (!d) return;
          const k = d.slice(0, 7);
          if (!byMonth[k]) byMonth[k] = { dc: 0, freight: 0, n: 0 };
          byMonth[k].dc += Number(r.dc_value) || 0;
          byMonth[k].freight += Number(r.freight_amount) || 0;
          byMonth[k].n += 1;
        });
        const months = Object.keys(byMonth).sort().reverse().slice(0, 6);
        if (!months.length) return null;
        const lbl = (k) => new Date(k + "-01").toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
        return (
          <div className="mb-4 overflow-x-auto rounded-lg border border-border bg-card" data-testid="booking-month-summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="px-4 py-2 text-left">Monthly Summary</th>
                  {months.map((k) => <th key={k} className="px-4 py-2 text-right">{lbl(k)}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="px-4 py-1.5 font-medium text-slate-600">Bookings</td>
                  {months.map((k) => <td key={k} className="px-4 py-1.5 text-right tabular-nums">{byMonth[k].n}</td>)}
                </tr>
                <tr className="border-t">
                  <td className="px-4 py-1.5 font-medium text-slate-600">DC Value</td>
                  {months.map((k) => <td key={k} className="px-4 py-1.5 text-right tabular-nums" data-testid="month-dc-value">{inr(byMonth[k].dc)}</td>)}
                </tr>
                <tr className="border-t">
                  <td className="px-4 py-1.5 font-medium text-slate-600">Freight</td>
                  {months.map((k) => <td key={k} className="px-4 py-1.5 text-right font-bold tabular-nums text-primary" data-testid="month-freight">{inr(byMonth[k].freight)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      <div className="mb-4 flex flex-wrap gap-4">
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Bookings — {periodLabel}</div>
          <div className="font-display text-2xl font-bold" data-testid="booking-count">{rows ? filtered.length : <Skeleton className="h-7 w-12" />}</div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Freight</div>
          <div className="font-display text-2xl font-bold text-primary" data-testid="booking-freight">{rows ? inr(totalFreight) : <Skeleton className="h-7 w-28" />}</div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Delivered / Pending</div>
          <div className="font-display text-2xl font-bold" data-testid="booking-status-split">
            <span className="text-emerald-600">{deliveredCount}</span> / <span className="text-amber-600">{pendingCount}</span>
          </div>
        </div>
        <div className="min-w-[220px] rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Top Routes</div>
          <div className="mt-1 space-y-0.5 text-sm" data-testid="booking-top-routes">
            {topRoutes.length === 0 ? <span className="text-muted-foreground">—</span> : topRoutes.map(([route, c]) => (
              <div key={route} className="flex justify-between gap-4"><span>{route}</span><span className="font-semibold tabular-nums">{c}</span></div>
            ))}
          </div>
        </div>
      </div>

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="booking-select-all" aria-label="Select all" />
              </TableHead>
              {["SL No.", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignor Name", "Customer Code", "Consignee Name", "Basis", "Payment Mode", "Cheque/DD Details", "Vehicle No.", "Vehicle Type", "PKG", "Invoice No.", "Invoice Date", "Invoice Value", "Freight Amount", "Bill No.", "Bill Date", "Bill Amount", "Delivery Date", "Delivery Status"].map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Freight Amount" || h === "Bill Amount" || h === "Invoice Value" ? "text-right" : ""}`}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3, 4].map((i) => (
                <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((c) => <TableCell key={c}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
              ))}
            {rows && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={22} className="py-12 text-center text-muted-foreground">No bookings in {periodLabel}.</TableCell>
              </TableRow>
            )}
            {paged.map((r, i) => (
              <TableRow key={r.id} data-testid="booking-row" className={!billFor(r) ? "bg-amber-50 hover:bg-amber-100/70" : ""} data-state={selected.has(r.id) ? "selected" : undefined}>
                <TableCell className="w-10">
                  <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid="booking-select-row" aria-label="Select booking" />
                </TableCell>
                <TableCell className="text-sm">{(page - 1) * PER_PAGE + i + 1}</TableCell>
                <TableCell className="text-sm">
                  <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${branchPillClass(r.office || "Chennai")}`} data-testid="booking-branch-pill">{r.office || "Chennai"}</span>
                </TableCell>
                <TableCell className="text-sm font-medium">{String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                <TableCell className="text-sm">{fmt(bookingDate(r))}</TableCell>
                <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                <TableCell className="text-sm">{r.consignor || r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm font-medium" data-testid="booking-cust-code">{custCode(r) || "—"}</TableCell>
                <TableCell className="text-sm">{r.consignee || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-basis-cell">{r.basis || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-payment-mode-cell">{r.basis === "PAID" ? r.payment_mode || "—" : "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-cheque-details-cell">{chequeDetails(r) || "—"}</TableCell>
                <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-vehicle-type-cell">{r.vehicle_type || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-pkg-cell">{r.packages || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-invoice-no-cell">{r.invoice_no || "—"}</TableCell>
                <TableCell className="text-sm">{r.invoice_date ? fmt(r.invoice_date) : "—"}</TableCell>
                <TableCell className="text-right text-sm tabular-nums" data-testid="booking-invoice-value-cell">{Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                <TableCell className="text-sm" data-testid="booking-bill-no-cell">{String(billFor(r)?.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—"}</TableCell>
                <TableCell className="text-sm">{billFor(r)?.issue_date ? fmt(billFor(r).issue_date) : "—"}</TableCell>
                <TableCell className="text-right text-sm tabular-nums" data-testid="booking-bill-amount-cell">{billFor(r)?.total != null ? inr(billFor(r).total) : "—"}</TableCell>
                <TableCell className="text-sm" data-testid="booking-delivery-date-cell">{r.delivery_date ? fmt(r.delivery_date) : "—"}</TableCell>
                <TableCell className="text-sm">{r.status || "—"}</TableCell>
              </TableRow>
            ))}
            {rows && filtered.length > 0 && (
              <TableRow className="bg-slate-100 font-bold">
                <TableCell colSpan={19} className="text-right text-sm">TOTAL FREIGHT</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-primary">{inr(totalFreight)}</TableCell>
                <TableCell colSpan={2} className="text-right text-sm">TOTAL BILL AMOUNT</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-primary" data-testid="booking-bill-total">
                  {inr((() => { const seen = new Set(); return filtered.reduce((s, r) => { const b = billFor(r); if (!b) return s; const k = b.id || b.invoice_no; if (seen.has(k)) return s; seen.add(k); return s + (Number(b.total) || 0); }, 0); })())}
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>
      {filtered.length > PER_PAGE && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1" data-testid="booking-pagination">
          <span className="text-xs text-muted-foreground">
            Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length} bookings · 20 per page
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPageNo(page - 1)} data-testid="booking-prev-page">← Prev</Button>
            <span className="text-sm font-semibold" data-testid="booking-page-label">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPageNo(page + 1)} data-testid="booking-next-page">Next →</Button>
            <span className="ml-2 text-xs text-muted-foreground">Jump to</span>
            <Input
              type="number"
              min={1}
              max={totalPages}
              placeholder="pg"
              className="h-8 w-16 text-center"
              value={jumpTo}
              onChange={(e) => setJumpTo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const n = Math.min(totalPages, Math.max(1, parseInt(jumpTo, 10) || 1));
                  setPageNo(n);
                  setJumpTo("");
                }
              }}
              data-testid="booking-jump-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const n = Math.min(totalPages, Math.max(1, parseInt(jumpTo, 10) || 1));
                setPageNo(n);
                setJumpTo("");
              }}
              data-testid="booking-jump-go"
            >
              Go
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
